import type { AuthResponse, LoginInput, Permission, SessionUser, SignupInput, UiMode } from '@batixa/domain';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, onUnauthorized, refreshSession, setAccessToken } from '../lib/api';

interface AuthState {
  user: SessionUser | null;
  loading: boolean;
  login(input: LoginInput): Promise<void>;
  signup(input: SignupInput): Promise<void>;
  logout(): Promise<void>;
  can(permission: Permission): boolean;
  /** Change le mode d'utilisation : seul l'affichage change, aucune donnée n'est touchée. */
  setUiMode(uiMode: UiMode): Promise<void>;
  /** Relit l'utilisateur (après un changement du nom de l'entreprise). */
  reloadUser(): Promise<void>;
  /** Nouvelle session reçue du serveur (changement de mot de passe). */
  adoptSession(session: AuthResponse): void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    onUnauthorized(() => setUser(null));
    refreshSession()
      .then((session) => setUser(session?.user ?? null))
      .finally(() => setLoading(false));
  }, []);

  const apply = useCallback((session: AuthResponse) => {
    setAccessToken(session.accessToken);
    setUser(session.user);
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      login: async (input) => apply(await api<AuthResponse>('/auth/login', { method: 'POST', body: input, auth: false })),
      signup: async (input) => apply(await api<AuthResponse>('/auth/signup', { method: 'POST', body: input, auth: false })),
      logout: async () => {
        await api('/auth/logout', { method: 'POST', auth: false }).catch(() => undefined);
        setAccessToken(null);
        setUser(null);
      },
      can: (permission) => !!user?.permissions.includes(permission),
      setUiMode: async (uiMode) => setUser(await api<SessionUser>('/auth/me', { method: 'PATCH', body: { uiMode } })),
      reloadUser: async () => setUser(await api<SessionUser>('/auth/me')),
      adoptSession: apply,
    }),
    [user, loading, apply],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth doit être utilisé dans <AuthProvider>');
  return ctx;
}
